import { useEffect, useRef } from 'react';

export interface UseClickOutsideOptions {
  enabled: boolean;
  ref: React.RefObject<HTMLElement | null>;
  /**
   * Chrome outside `ref` that must not dismiss, plus the toggle owning the open
   * state — without it the press dismisses and the toggle's click reopens.
   * Exact strings, so a label containing a quote cannot break `closest()`.
   */
  ignoreAriaLabels?: readonly string[];
  onOutsideClick: () => void;
}

/** Walks the whole chain: `closest()` stops at the nearest labelled ancestor. */
const hasIgnoredAncestor = (
  target: Element,
  labels: readonly string[],
): boolean => {
  for (
    let node: Element | null = target;
    node !== null;
    node = node.parentElement
  ) {
    const label = node.getAttribute('aria-label');
    if (label !== null && labels.includes(label)) {
      return true;
    }
  }
  return false;
};

/**
 * On `pointerdown`, not `click`, so dismissal precedes focus; the trade-off is
 * that a press which never becomes a click dismisses too.
 */
export function useClickOutside(options: UseClickOutsideOptions): void {
  const { enabled, ref, ignoreAriaLabels, onOutsideClick } = options;

  // Via refs so an unmemoized callback or inline array does not re-subscribe.
  const onOutsideClickRef = useRef(onOutsideClick);
  onOutsideClickRef.current = onOutsideClick;
  const ignoreAriaLabelsRef = useRef(ignoreAriaLabels);
  ignoreAriaLabelsRef.current = ignoreAriaLabels;

  useEffect(() => {
    if (!enabled) {
      return;
    }

    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target;
      if (!(target instanceof Element)) {
        return;
      }
      if (ref.current?.contains(target) === true) {
        return;
      }
      const ignoredLabels = ignoreAriaLabelsRef.current;
      if (
        ignoredLabels !== undefined &&
        hasIgnoredAncestor(target, ignoredLabels)
      ) {
        return;
      }
      onOutsideClickRef.current();
    };

    document.addEventListener('pointerdown', handlePointerDown);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
    };
  }, [enabled, ref]);
}
